import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';

const monoFont = "'IBM Plex Mono', monospace";

interface CmsDashboardProps {
  onNavigate: (tab: string) => void;
  username: string;
}

interface DashboardData {
  homeImageCount: number;
  homePublished: boolean;
  aboutFilled: boolean;
  aboutPublished: boolean;
  sectionsActiveCount: number;
  sectionsDraftCount: number;
  appearanceCustomized: boolean;
  seoReady: boolean;
  approvalPagesCount: number;
  approvalPendingCount: number;
  lastAccessAt: string | null;
  siteName: string;
  totalImages: number;
}

function DashboardSkeleton() {
  const pulse = { animation: 'pulse 1.5s ease-in-out infinite', backgroundColor: 'rgba(247,246,242,0.08)' };
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', fontFamily: monoFont }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ ...pulse, width: 180, height: 18, marginBottom: 8 }} />
        <div style={{ ...pulse, width: 140, height: 10 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5px', background: 'rgba(247,246,242,0.12)', marginBottom: '0.5px' }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{ background: '#0A0A0A', padding: 22 }}>
            <div style={{ ...pulse, width: 60, height: 10, marginBottom: 12 }} />
            <div style={{ ...pulse, width: 100, height: 16, marginBottom: 12 }} />
            <div style={{ ...pulse, width: 80, height: 10 }} />
          </div>
        ))}
      </div>
      <div style={{ background: '#0A0A0A', padding: 22, borderTop: '0.5px solid rgba(247,246,242,0.12)' }}>
        <div style={{ ...pulse, width: 200, height: 12, marginBottom: 12 }} />
        <div style={{ ...pulse, width: '60%', height: 3, marginBottom: 8 }} />
        <div style={{ ...pulse, width: 100, height: 10 }} />
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

export function CmsDashboard({ onNavigate, username }: CmsDashboardProps) {
  const { session } = useAuth();
  const { plan, isPro, isFree, limits, loading: planLoading } = usePlan();
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    if (!session) return;
    const uid = session.user.id;
    const load = async () => {
      const [
        { data: homeImages },
        { data: aboutPage },
        { data: projects },
        { data: settings },
        { data: clientPages },
        { data: profile },
      ] = await Promise.all([
        supabase.from('home_images').select('id').eq('user_id', uid),
        supabase.from('about_pages').select('bio_blocks, photo_url, status').eq('user_id', uid).maybeSingle(),
        supabase.from('projects').select('id, published, section_slug').eq('user_id', uid),
        supabase.from('site_settings').select('site_title, site_description, font_family, text_color, hover_transition, menu_position_h, menu_position_v').eq('user_id', uid).maybeSingle(),
        supabase.rpc('get_client_pages_safe'),
        supabase.from('user_profiles').select('site_name, last_access_at').eq('user_id', uid).maybeSingle(),
      ]);

      const bioBlocks = aboutPage?.bio_blocks as any[] | null;
      const aboutFilled = !!(
        (bioBlocks && bioBlocks.length > 0 && bioBlocks.some((b: any) => b.text?.trim())) ||
        aboutPage?.photo_url
      );

      const publishedProjects = (projects || []).filter((p: any) => p.published);
      const draftProjects = (projects || []).filter((p: any) => !p.published);
      const sectionSlugsActive = new Set(publishedProjects.map((p: any) => p.section_slug));
      const sectionSlugsDraft = new Set(draftProjects.map((p: any) => p.section_slug));
      // Remove active slugs from draft count
      sectionSlugsActive.forEach(s => sectionSlugsDraft.delete(s));

      const appearanceCustomized = settings ? (
        settings.font_family !== 'IBM Plex Mono' ||
        settings.text_color !== '#000000' ||
        settings.hover_transition !== 'quickflip' ||
        settings.menu_position_h !== 'central' ||
        settings.menu_position_v !== 'centro'
      ) : false;

      const seoReady = !!(
        settings?.site_title &&
        settings.site_title !== 'CRIAAA — Portfolio' &&
        settings?.site_description &&
        settings.site_description !== 'criaaa.com — Portfolio de fotografia contemporânea'
      );

      const homePublished = publishedProjects.length > 0 || (homeImages?.length ?? 0) > 0;

      setData({
        homeImageCount: homeImages?.length ?? 0,
        homePublished,
        aboutFilled,
        aboutPublished: aboutPage?.status === 'published',
        sectionsActiveCount: sectionSlugsActive.size,
        sectionsDraftCount: sectionSlugsDraft.size,
        appearanceCustomized,
        seoReady,
        approvalPagesCount: (clientPages as any[])?.length ?? 0,
        approvalPendingCount: 0,
        lastAccessAt: profile?.last_access_at || null,
        siteName: profile?.site_name || '',
        totalImages: homeImages?.length ?? 0,
      });
    };
    load();
  }, [session]);

  if (!data || planLoading) return <DashboardSkeleton />;

  const formatLastAccess = () => {
    if (!data.lastAccessAt) return '—';
    const d = new Date(data.lastAccessAt);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) {
      const mins = Math.floor(diffMs / 60000);
      return mins < 1 ? 'agora' : `há ${mins}min`;
    }
    if (diffH < 24) return `há ${diffH}h`;
    if (diffH < 48) return 'ontem';
    return d.toLocaleDateString('pt-BR');
  };

  const isOnline = data.sectionsActiveCount > 0 || data.homePublished;

  const cards = [
    {
      title: 'HOME',
      value: `${data.homeImageCount} obras`,
      status: data.homePublished ? 'Publicado' : 'Rascunho',
      statusColor: data.homePublished ? '#00E87A' : 'rgba(247,246,242,0.4)',
      tab: 'home',
    },
    {
      title: 'ABOUT',
      value: data.aboutFilled ? 'Preenchido' : 'Não preenchido',
      status: data.aboutPublished ? 'Publicado' : 'Rascunho',
      statusColor: data.aboutPublished ? '#00E87A' : 'rgba(247,246,242,0.4)',
      tab: 'about',
    },
    {
      title: 'SEÇÕES',
      value: `${data.sectionsActiveCount} ativas`,
      status: data.sectionsDraftCount > 0 ? `${data.sectionsDraftCount} rascunho` : '',
      statusColor: data.sectionsDraftCount > 0 ? 'rgba(247,246,242,0.4)' : 'transparent',
      tab: 'menu',
    },
    {
      title: 'APARÊNCIA',
      value: data.appearanceCustomized ? 'Personalizada' : 'Padrão',
      status: data.appearanceCustomized ? 'Configurada' : 'Padrão',
      statusColor: data.appearanceCustomized ? '#00E87A' : 'rgba(247,246,242,0.4)',
      tab: 'aparencia',
    },
    {
      title: 'CONFIGURAÇÕES',
      value: data.seoReady ? 'SEO pronto' : 'Incompleto',
      status: data.seoReady ? 'Completo' : 'Incompleto',
      statusColor: data.seoReady ? '#00E87A' : 'rgba(247,246,242,0.4)',
      tab: 'configuracoes',
    },
    {
      title: 'APROVAÇÃO',
      value: data.approvalPagesCount > 0 ? `${data.approvalPagesCount} páginas` : 'Nenhuma página',
      status: data.approvalPendingCount > 0 ? `${data.approvalPendingCount} pendente` : 'Em dia',
      statusColor: data.approvalPendingCount > 0 ? '#F0E614' : '#00E87A',
      tab: 'aprovacao',
    },
  ];

  // Plan card calculations
  const maxImages = limits.maxImagesPerGallery ?? 999;
  const usedImages = data.totalImages;
  const usagePct = Math.min(Math.round((usedImages / maxImages) * 100), 100);
  const barColor = usagePct >= 90 ? '#FF3F2D' : usagePct >= 70 ? '#F0E614' : '#00E87A';
  const planLabel = plan === 'custom' ? 'Plano Pro' : plan === 'pro' ? 'Plano Criador' : 'Plano Free';

  const quickActions = [
    { label: '+ ADICIONAR OBRA', tab: 'home' },
    { label: '+ CRIAR SEÇÃO', tab: 'menu' },
    { label: 'CONVIDAR ALGUÉM', tab: 'perfil' },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', fontFamily: monoFont }}>
      {/* Topbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 300, color: '#F7F6F2', margin: 0 }}>
            {data.siteName || username}
          </h1>
          <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(247,246,242,0.4)', marginTop: 4 }}>
            último acesso: {formatLastAccess()}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isOnline && (
            <span style={{
              fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em',
              color: '#00E87A', border: '0.5px solid #00E87A', padding: '3px 9px',
            }}>
              ● ONLINE
            </span>
          )}
          <a
            href={`/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em',
              color: 'rgba(247,246,242,0.4)', border: '0.5px solid rgba(247,246,242,0.4)',
              padding: '3px 9px', textDecoration: 'none', transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F7F6F2'; e.currentTarget.style.color = '#0A0A0A'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(247,246,242,0.4)'; }}
          >
            VER SITE →
          </a>
        </div>
      </div>

      {/* 6 Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5px', background: 'rgba(247,246,242,0.12)', marginBottom: '0.5px' }}>
        {cards.map(card => (
          <button
            key={card.title}
            onClick={() => onNavigate(card.tab)}
            style={{
              fontFamily: monoFont, padding: 22, background: '#0A0A0A', border: 'none',
              cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s ease',
              borderRadius: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(247,246,242,0.04)')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0A0A0A')}
          >
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(247,246,242,0.4)', marginBottom: 4 }}>
              {card.title}
            </div>
            <div style={{ fontSize: 16, color: '#F7F6F2', fontWeight: 300 }}>
              {card.value}
            </div>
            {card.status && (
              <div style={{ fontSize: 12, color: card.statusColor, marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: card.statusColor, flexShrink: 0 }} />
                {card.status}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Plan Card */}
      <div style={{ background: '#0A0A0A', padding: 22, borderTop: '0.5px solid rgba(247,246,242,0.12)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#F7F6F2' }}>
              {planLabel}
            </span>
            <span style={{
              fontSize: 12, textTransform: 'uppercase', color: 'rgba(247,246,242,0.4)',
              border: '0.5px solid rgba(247,246,242,0.4)', padding: '2px 7px',
            }}>
              ATUAL
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {isFree ? (
              <>
                <button
                  onClick={() => onNavigate('planos')}
                  style={{
                    fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em',
                    color: '#0057FF', border: '0.5px solid #0057FF', background: 'none',
                    padding: '8px 16px', cursor: 'pointer', borderRadius: 0,
                  }}
                >
                  FAZER UPGRADE
                </button>
                <button
                  onClick={() => onNavigate('planos')}
                  style={{
                    fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em',
                    color: 'rgba(247,246,242,0.4)', border: 'none', background: 'none',
                    padding: '8px 0', cursor: 'pointer', transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#0057FF')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.4)')}
                >
                  VER PLANOS
                </button>
              </>
            ) : (
              <button
                onClick={() => onNavigate('planos')}
                style={{
                  fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em',
                  color: 'rgba(247,246,242,0.4)', border: '0.5px solid rgba(247,246,242,0.4)',
                  background: 'none', padding: '8px 16px', cursor: 'pointer', borderRadius: 0,
                }}
              >
                GERENCIAR PLANO
              </button>
            )}
          </div>
        </div>

        {isFree && (
          <>
            <div style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)', marginBottom: 8 }}>
              {usedImages} de {maxImages} imagens usadas · {limits.maxGalleries ?? '∞'} galerias
            </div>
            <div style={{ width: '100%', height: 3, background: 'rgba(247,246,242,0.12)', marginBottom: 4 }}>
              <div style={{ width: `${usagePct}%`, height: '100%', background: barColor, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ fontSize: 12, color: barColor }}>
              {usagePct}% do limite atingido
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5px', background: 'rgba(247,246,242,0.12)', marginTop: '0.5px' }}>
        {quickActions.map(action => (
          <button
            key={action.label}
            onClick={() => onNavigate(action.tab)}
            style={{
              fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em',
              color: 'rgba(247,246,242,0.4)', background: '#0A0A0A', border: 'none',
              padding: 14, cursor: 'pointer', transition: 'all 0.15s ease', borderRadius: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#F7F6F2'; e.currentTarget.style.background = 'rgba(247,246,242,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(247,246,242,0.4)'; e.currentTarget.style.background = '#0A0A0A'; }}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}
